import type { Instance } from "flatpickr/dist/types/instance"

/**
 * Renders the flatpickr calendar via the Popover API on the #top-layer, like the PDS popover does.
 * Otherwise, the calendar gets clipped by scroll containers and modals it is rendered within.
 * The calendar stays a DOM child of the datepicker, so it remains part of the focus and click handling of a modal.
 */
export const createTopLayerCalendar = () => {
	let stopRepositioning: (() => void) | undefined

	// flatpickr calls this after onOpen and expects the calendar to be positioned
	const position = (instance: Instance) => {
		// with wrap: true the element is the datepicker wrapper, whereas the input itself is hidden and has no size
		const anchorRect = instance.element.getBoundingClientRect()
		const { height, width } = instance.calendarContainer.getBoundingClientRect()
		const hasSpaceBelow = window.innerHeight - anchorRect.bottom >= height
		const showOnTop = !hasSpaceBelow && anchorRect.top > height
		const top = Math.max(0, showOnTop ? anchorRect.top - height : anchorRect.bottom)
		// align the calendar with the start edge of the input
		const isRtl = instance.element.closest("[dir]")?.getAttribute("dir") === "rtl"
		const preferredLeft = isRtl ? anchorRect.right - width : anchorRect.left
		const left = Math.max(0, Math.min(preferredLeft, window.innerWidth - width))

		instance.calendarContainer.style.top = `${top}px`
		instance.calendarContainer.style.left = `${left}px`
	}

	const stop = () => {
		stopRepositioning?.()
		stopRepositioning = undefined
	}

	// on mobile devices flatpickr uses the native date input and builds no calendar
	const onReady = (_dates: Date[], _dateString: string, instance: Instance) => {
		if (instance.isMobile) {
			return
		}
		instance.calendarContainer.setAttribute("popover", "manual")
	}

	const onOpen = (_dates: Date[], _dateString: string, instance: Instance) => {
		// a repeated open must not leave the listeners of the previous one behind
		stop()
		instance.calendarContainer.showPopover?.()

		const reposition = () => position(instance)
		// the capture phase also catches the scrolling of any ancestor
		window.addEventListener("scroll", reposition, true)
		window.addEventListener("resize", reposition)
		stopRepositioning = () => {
			window.removeEventListener("scroll", reposition, true)
			window.removeEventListener("resize", reposition)
		}
	}

	const onClose = (_dates: Date[], _dateString: string, instance: Instance) => {
		stop()
		if (!instance.isMobile) {
			instance.calendarContainer.hidePopover?.()
		}
	}

	return { onClose, onOpen, onReady, position, stop }
}
